import { MiddlewareAPI } from "@/lib/axios";
import {
  ICommonListApiRes,
  ICommonQueryParams,
  NetworkMethodEnum,
} from "@/types";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";
import { generateQueryParamsForPagination } from "@/utils/pagination";
import { CUSTOMER_STATUS_ACTIVE } from "../constants";
import { ICustomer, ICustomerForm, ICreditControlResponse } from "../types";

export const URL = {
  customers: "/AccountApi",
  creditControl: "/GetCreditControlByAccount",
};

export const createCustomer = async (
  data: ICustomerForm
): Promise<ICustomer> => {
  const payload = {
    name: data.name,
    emailaddress1: data.emailaddress1,
    telephone1: data.telephone1,
    address1_line1: data.address1_line1,
    address1_city: data.address1_city,
    address1_postalcode: data.address1_postalcode,
    address1_country: data.address1_country,
    statuscode: data.statuscode ?? CUSTOMER_STATUS_ACTIVE,
  };
  return await mutator(NetworkMethodEnum.POST, URL.customers, payload);
};

export const updateCustomer = async (data: {
  id: string;
  payload: Partial<ICustomerForm>;
}): Promise<ICustomer> => {
  const updatePayload = {
    id: data.id,
    ...data.payload,
  };
  return await mutator(NetworkMethodEnum.PATCH, URL.customers, updatePayload);
};

export const changeCustomerStatus = async (data: {
  id: string;
  payload: { statuscode: number };
}): Promise<ICustomer> => {
  const statusPayload = {
    id: data.id,
    statuscode: data.payload.statuscode,
  };

  const response = await MiddlewareAPI.patch<ICustomer>(
    URL.customers,
    statusPayload
  );
  return response.data;
};

export const deleteCustomer = async (id: string): Promise<void> => {
  await MiddlewareAPI.delete(URL.customers, {
    data: { id },
  });
};

export const getCustomerList = async (
  queryParams?: ICommonQueryParams
): Promise<ICommonListApiRes<ICustomer>> => {
  const query = generateQueryParamsForPagination(queryParams);
  return await fetcher(
    `${URL.customers}${query}&$orderby=createdon desc&$expand=primarycontactid`,
    queryParams?.pageSize
  );
};

export const getCustomerDetail = async (id: string): Promise<ICustomer> => {
  const response = await MiddlewareAPI.get<ICustomer>(URL.customers, {
    params: { id },
  });
  return response.data;
};

/**
 * Get customer count by status code (optional filter)
 */
export const getCustomerCountByStatusApi = async (
  statuscode?: number
): Promise<any> => {
  const baseURL = `${URL.customers}?$apply=aggregate($count as count)`;
  const url =
    statuscode !== undefined
      ? `${baseURL}&$filter=statuscode eq ${statuscode}`
      : baseURL;
  return await fetcher(url);
};

/**
 * Get counts grouped by all status codes
 */
export const getAllCustomerCountsByStatusApi = async (): Promise<any> => {
  const url = `${URL.customers}?$apply=groupby((statuscode), aggregate($count as count))`;
  return await fetcher(url);
};

/**
 * Get customer/account details by ID (for details page)
 */
export const getCustomerDetailByIdApi = async (
  accountId: string
): Promise<ICustomer> => {
  return await fetcher(`${URL.customers}?id=${accountId}&$expand=dogma_DealersCurrency($select=dogma_name,dogma_currencysymbol,dogma_isocurrencycode,dogma_exchangerate,dogma_vatpercentage),primarycontactid($select=contactid,fullname,emailaddress1,telephone1)`);
};

/**
 * Get credit control history by account ID
 */
export const getCreditControlByAccountApi = async (
  accountId: string
): Promise<ICreditControlResponse> => {
  return await fetcher(`${URL.creditControl}?accountId=${accountId}`);
};
